import { useState, useEffect } from 'react';
import { ArrowLeftRight, Send, AlertTriangle } from 'lucide-react';
import ModalShell from './ui/ModalShell';
import Dropdown from './receptura/Dropdown';
import useJeMobil from '../hooks/useJeMobil';

const BARVA = '#6366F1';

/**
 * Návrh prohození / předání směny kolegovi.
 *
 * Rám (portál do body, zdola na mobilu / na střed na desktopu, sklo, z-index
 * nad lištou, Esc, zámek scrollu) drží `ui/ModalShell` — viz tamní komentář.
 * Dřív si modal kreslil `position: fixed` sám uvnitř stromu volajícího, takže
 * ho v „Můj měsíc" uvěznila skleněná karta kalendáře (`backdrop-filter`
 * dělá z fixed pozici vůči kartě) a ořízl její `overflow: hidden`.
 *
 * Výběr kolegy i jeho směny jede přes `receptura/Dropdown` — nativní `<select>`
 * otevírá systémový seznam, který se na telefonu roztáhne přes celý modal.
 *
 * Props:
 *   smena     — moje směna, která se nabízí
 *   onClose   — zavření
 *   onSuccess — po úspěšném vytvoření návrhu (refresh dat volajícího)
 *   isDark    — tmavé téma
 */
export default function ProhoditSmenuModal({ smena, onClose, onSuccess, isDark }) {
    const isMobile = useJeMobil();
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const [typ, setTyp] = useState('prohoz'); // 'prohoz' | 'predat'
    const [kolegove, setKolegove] = useState([]);
    const [prijemceId, setPrijemceId] = useState('');
    const [kolegoveSmeny, setKolegoveSmeny] = useState([]);
    const [prijemceSmenaId, setPrijemceSmenaId] = useState('');
    const [poznamka, setPoznamka] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    useEffect(() => {
        fetch('/api/zamestnanci')
            .then(r => r.json())
            .then(data => {
                const list = (Array.isArray(data) ? data : []).filter(
                    z => z.id !== smena.zamestnanec_id && z.stav === 'aktivni'
                );
                setKolegove(list);
            }).catch(() => setKolegove([]));
    }, [smena.zamestnanec_id]);

    useEffect(() => {
        if (!prijemceId || typ !== 'prohoz') { setKolegoveSmeny([]); setPrijemceSmenaId(''); return; }
        fetch(`/api/shifts/zamestnance/${prijemceId}/budouci`)
            .then(r => r.json())
            .then(data => setKolegoveSmeny(Array.isArray(data) ? data : []))
            .catch(() => setKolegoveSmeny([]));
    }, [prijemceId, typ]);

    const handleSubmit = async () => {
        setError(null);
        if (!prijemceId) { setError('Vyber kolegu'); return; }
        if (typ === 'prohoz' && !prijemceSmenaId) { setError('Vyber kolegovu směnu pro prohoz'); return; }

        setLoading(true);
        const body = {
            typ,
            prijemce_zamestnanec_id: Number(prijemceId),
            ...(typ === 'prohoz' && { prijemce_smena_id: Number(prijemceSmenaId) }),
            ...(poznamka.trim() && { poznamka: poznamka.trim() }),
        };
        try {
            const res = await fetch(`/api/shifts/${smena.id}/navrh-prohozeni`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const b = await res.json().catch(() => ({}));
                setError(b.error || 'Odeslání selhalo');
                setLoading(false);
                return;
            }
            onSuccess?.();
            onClose();
        } catch {
            setError('Chyba připojení');
            setLoading(false);
        }
    };

    const fmtDate = (d) => {
        if (!d) return '—';
        const s = typeof d === 'string' ? d.split('T')[0] : d;
        return new Date(s + 'T12:00:00').toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', weekday: 'short' });
    };
    const fmtTime = (t) => t ? String(t).substring(0, 5) : '—';

    const datumStr = typeof smena.datum === 'string' ? smena.datum.substring(0, 10) : smena.datum;
    const smenaLabel = `${fmtDate(datumStr)} · ${fmtTime(smena.cas_od)}–${fmtTime(smena.cas_do)}`;

    const popisek = (text) => (
        <div style={{
            marginBottom: 6, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase', color: mutedColor,
        }}>{text}</div>
    );

    const paticka = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={loading} style={{
                minHeight: isMobile ? 44 : 38, padding: '0 16px', borderRadius: 8,
                border: `1px solid ${borderColor}`, background: 'transparent',
                color: secondaryColor, fontSize: 13, cursor: 'pointer',
            }}>Zrušit</button>
            <button type="button" onClick={handleSubmit} disabled={loading} style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                minHeight: isMobile ? 44 : 38, padding: '0 16px', borderRadius: 8,
                border: 'none', background: BARVA, color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
            }}>
                <Send size={13} />
                {loading ? 'Odesílám…' : 'Odeslat návrh'}
            </button>
        </div>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Prohodit / předat směnu"
            ikona={ArrowLeftRight} barva={BARVA} sirka={480}
            zavritLze={!loading} paticka={paticka}>
            <div style={{ padding: isMobile ? 16 : '16px 16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ fontSize: 12.5, color: secondaryColor }}>
                    Nabízíš: <b style={{ color: 'var(--text-primary)' }}>{smenaLabel}</b>
                </div>

                {/* Typ návrhu */}
                <div style={{ display: 'flex', gap: 8 }}>
                    {[{ id: 'prohoz', label: 'Prohodit za jinou' }, { id: 'predat', label: 'Předat bez náhrady' }].map(m => (
                        <button key={m.id} type="button" onClick={() => setTyp(m.id)} style={{
                            flex: 1, minHeight: isMobile ? 44 : 38, padding: '0 10px', borderRadius: 8,
                            border: typ === m.id ? `1.5px solid ${BARVA}` : `1px solid ${borderColor}`,
                            background: typ === m.id ? `${BARVA}18` : 'transparent',
                            color: typ === m.id ? BARVA : 'var(--text-primary)',
                            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                        }}>{m.label}</button>
                    ))}
                </div>

                {/* Kolega */}
                <div>
                    {popisek('Kolega')}
                    <Dropdown
                        value={prijemceId}
                        onChange={(v) => { setPrijemceId(v); setError(null); }}
                        options={kolegove.map(z => ({ value: String(z.id), label: `${z.jmeno} ${z.prijmeni}` }))}
                        placeholder={kolegove.length ? '— Vyber —' : 'Načítám…'}
                        isDark={isDark} accent={BARVA} height={isMobile ? 44 : 38}
                    />
                </div>

                {/* Kolegova směna — jen u prohozu */}
                {typ === 'prohoz' && prijemceId && (
                    <div>
                        {popisek('Kolegova směna (kterou chceš výměnou)')}
                        {kolegoveSmeny.length === 0 ? (
                            <div style={{
                                padding: '9px 11px', borderRadius: 8, fontSize: 12, lineHeight: 1.45,
                                color: secondaryColor, border: `1px solid ${borderColor}`,
                                background: isDark ? 'rgba(255,255,255,.04)' : 'var(--surface-base)',
                            }}>
                                Kolega nemá žádnou směnu &gt; 12 h do začátku.
                            </div>
                        ) : (
                            <Dropdown
                                value={prijemceSmenaId}
                                onChange={(v) => { setPrijemceSmenaId(v); setError(null); }}
                                options={kolegoveSmeny.map(s => ({
                                    value: String(s.id),
                                    label: `${fmtDate(s.datum)} · ${fmtTime(s.cas_od)}–${fmtTime(s.cas_do)}`,
                                    popis: s.sablon?.nazev || undefined,
                                }))}
                                placeholder="— Vyber —"
                                isDark={isDark} accent={BARVA} height={isMobile ? 44 : 38}
                            />
                        )}
                    </div>
                )}

                {/* Poznámka */}
                <div>
                    {popisek('Poznámka (volitelné)')}
                    <textarea value={poznamka} onChange={e => { setPoznamka(e.target.value); setError(null); }}
                        rows={3} maxLength={500}
                        placeholder="Krátká zpráva pro kolegu…"
                        style={{
                            display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
                            resize: 'none', border: `1px solid ${borderColor}`, borderRadius: 8,
                            padding: '10px 12px',
                            // Pod 16 px iOS při fokusu stránku přizoomuje a rozvržení se rozjede.
                            fontSize: 16, lineHeight: 1.4, color: 'var(--text-primary)',
                            background: isDark ? 'rgba(255,255,255,.05)' : 'var(--surface-base)',
                            fontFamily: 'inherit', outline: 'none',
                        }}
                    />
                </div>

                {/* Info — nekvalifikovaný zástup */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 7, padding: '9px 11px',
                    background: isDark ? 'rgba(255,184,0,.08)' : 'rgba(255,184,0,.12)',
                    border: '1px solid rgba(255,184,0,.3)', borderRadius: 8,
                    fontSize: 11.5, lineHeight: 1.45, color: '#B57C00',
                }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>Pokud kolega nebude mít pro tuto směnu kvalifikaci, jeho akceptace vyžaduje ještě schválení admina (zástup).</span>
                </div>

                {error && (
                    <div style={{
                        padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.4,
                        background: 'rgba(216,90,48,.12)', border: '1px solid rgba(216,90,48,.3)', color: '#D85A30',
                    }}>{error}</div>
                )}
            </div>
        </ModalShell>
    );
}
